import { useMemo, useEffect, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'
import * as yup from 'yup'
import { debounce } from 'lodash'
import { Form, Dialog, Text, Button, Tooltip, Separator } from '@/ui'
import { faInfo, faCheck } from '@fortawesome/pro-regular-svg-icons'
import {
  JournalEntry,
  JournalEntryLineItem,
  JournalEntryType,
  SubmitJournalEntryBody,
} from '../types'
import {
  formatCurrency,
  addCurrencyArray,
  dollarsToCents,
} from '@/utils/currencyHelpers'
import {
  getJournalEntryInitialValues,
  COMMON_JOURNAL_ENTRY_TYPES,
} from '../service'
import {
  fetchSingleTransaction,
  FETCH_SINGLE_TRANSACTION_KEY,
} from '@/actions/admin/transactionActions'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { getCurrentUser } from '@/selectors/user.selectors'
import {
  updateJournalEntry,
  UPDATE_JOURNAL_ENTRY_KEY,
  createJournalEntry,
} from '@/actions/admin/journalEntryActions'
import { JournalEntryLineInputs } from './JournalEntryLineInputs'

// TODO: Migrate to V2
import {
  JournalEntryUpdateType,
  MaybeJournalEntry,
} from '@/features/admin/paginated-transaction-table/RowDrawer'
import { valsToDropdownOptions } from '@/componentsV1/BaseComponents/Dropdown'
import { getFieldName } from '@/componentsV1/BaseComponents'

interface EditJournalEntryModalProps {
  open: boolean
  onClose: () => void
  journalEntry?: JournalEntry
  transactionId?: string
  onUpdate?: (
    journalEntry: MaybeJournalEntry,
    type: JournalEntryUpdateType
  ) => void
}

export const EditJournalEntryModal = ({
  open,
  onClose,
  journalEntry,
  transactionId,
  onUpdate = (_: MaybeJournalEntry, _2?: JournalEntryUpdateType) => undefined,
}: EditJournalEntryModalProps) => {
  const loading = useReselector(selectIsFetchingForKeys, [
    UPDATE_JOURNAL_ENTRY_KEY,
    FETCH_SINGLE_TRANSACTION_KEY,
  ])
  const dispatch = useAppDispatch()
  const { userId } = useParams<{ userId: string }>()
  const currentUser = useReselector(getCurrentUser)
  const [transactionObj, setTransactionObj] = useState<{
    error?: string
    description: string
  }>({
    error: undefined,
    description: '',
  })
  const [journalEntryLinesToDelete, setJournalEntryLinesToDelete] = useState<
    number[]
  >([])

  const formik = useFormik({
    initialValues: getJournalEntryInitialValues(journalEntry, transactionId),
    onSubmit: async (values) => {
      if (!userId) {
        return
      }
      const body: SubmitJournalEntryBody = {
        details: values.details,
        journalEntryDate: new Date(values.journalEntryDate).toISOString(),
        lastUpdatedBy: currentUser?.id,
        userId: parseInt(userId),
      }
      if (!journalEntry?.automated) {
        if (values.type) {
          body.details = `${values.type} ${values.details}`
        }
        body.journalEntryLines = values.journalEntryLines.map((line) => {
          const formattedLine: Partial<JournalEntryLineItem> = {
            financialAccountId: null,
            transactionCategoryId: null,
          }
          const debitAmount = parseFloat(line.debitAmount)
          const creditAmount = parseFloat(line.creditAmount)
          if (debitAmount) {
            formattedLine.amountInCents = dollarsToCents(debitAmount)
            formattedLine.entryType = JournalEntryType.DEBIT
          } else {
            formattedLine.amountInCents = dollarsToCents(creditAmount)
            formattedLine.entryType = JournalEntryType.CREDIT
          }
          if (line.accountOrCategoryId?.includes('fin')) {
            formattedLine.financialAccountId = parseInt(
              line.accountOrCategoryId
            )
          } else if (line.accountOrCategoryId?.includes('cat')) {
            formattedLine.transactionCategoryId = parseInt(
              line.accountOrCategoryId
            )
          }
          if (line.id) {
            formattedLine.id = line.id
          }
          if (journalEntry?.id) {
            formattedLine.journalEntryId = journalEntry?.id
          }
          return formattedLine
        })
        body.transactionId = values.transactionId
          ? parseInt(values.transactionId)
          : undefined
        if (journalEntryLinesToDelete.length > 0) {
          body.journalEntryLinesToDelete = journalEntryLinesToDelete
        } else if (body.journalEntryLinesToDelete) {
          delete body.journalEntryLinesToDelete
        }
      }
      let res
      if (journalEntry) {
        res = await updateJournalEntry(journalEntry.id, body)(dispatch)
      } else {
        res = await createJournalEntry(body)(dispatch)
      }
      if (res) {
        onUpdate(
          res,
          journalEntry
            ? JournalEntryUpdateType.UPDATE
            : JournalEntryUpdateType.CREATE
        )
        onClose()
      }
    },
  })

  const debouncedGetTransaction = useMemo(
    () =>
      debounce(async (transId: string | undefined | null) => {
        if (journalEntry?.automated) return // dont call API if journal entry is automated (no transaction field shown)

        if (!transId) {
          return setTransactionObj({
            error: undefined,
            description: '',
          })
        }

        // if trans id has decimals, don't call API
        if (parseFloat(transId) % 1 !== 0) return

        const retrievedTransaction =
          await fetchSingleTransaction(transId)(dispatch)

        if (!retrievedTransaction || !retrievedTransaction.data) {
          return setTransactionObj({
            error: 'Transaction not found.',
            description: '',
          })
        }

        if (userId && retrievedTransaction.data.userId !== parseInt(userId)) {
          return setTransactionObj({
            error: 'Transaction is connected to a different user.',
            description: '',
          })
        }
        return setTransactionObj({
          error: undefined,
          description: retrievedTransaction.data.description.substring(0, 160), // only display up to first 160 chars so it fits on screen
        })
      }, 500),
    [dispatch, userId, journalEntry?.automated]
  )

  useEffect(() => {
    debouncedGetTransaction(formik.values.transactionId)
  }, [formik.values.transactionId, debouncedGetTransaction])

  useEffect(() => {
    if (formik.errors.transactionId && transactionObj.error) return
    if (formik.errors.transactionId !== transactionObj.error) {
      formik.setFieldError('transactionId', transactionObj.error)
      // if there is an error but field hasn't been touched yet, still show error state
      if (!formik.touched.transactionId) {
        formik.setFieldTouched('transactionId')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transactionObj.error,
    formik.errors.transactionId,
    formik.touched.transactionId,
  ]) // requires formik but causes too many triggers

  const totals = useMemo(() => {
    const debitTotal = addCurrencyArray(
      formik.values.journalEntryLines.map((line) => line.debitAmount)
    )
    const creditTotal = addCurrencyArray(
      formik.values.journalEntryLines.map((line) => line.creditAmount)
    )
    return {
      debitTotal: formatCurrency(debitTotal),
      creditTotal: formatCurrency(creditTotal),
    }
  }, [formik.values])

  const showManualFields = !journalEntry?.automated

  const totalError =
    showManualFields && totals.debitTotal !== totals.creditTotal

  const headerText = journalEntry
    ? 'Edit journal entry'
    : 'Create journal entry'

  const submitButtonText = journalEntry ? 'Save Changes' : 'Create'

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="p-8" size="lg">
        <FormikProvider value={formik}>
          <Dialog.Title>
            <Text variant={TypographyStyles.H2} className="mb-4">
              {headerText}
            </Text>
          </Dialog.Title>
          <div className="flex flex-col gap-4">
            <Form.DateInput
              name={getFieldName<typeof formik.values>('journalEntryDate')}
              label="Journal Entry Date"
              schema={yup.string().required('Required')}
              className="w-full"
              required
            />

            {showManualFields && (
              <JournalEntryLineInputs
                formik={formik}
                userId={userId}
                totals={totals}
                totalError={totalError}
                setJournalEntryLinesToDelete={setJournalEntryLinesToDelete}
              />
            )}

            <Separator />

            {showManualFields && (
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <Form.Select
                    name="type"
                    options={valsToDropdownOptions(COMMON_JOURNAL_ENTRY_TYPES)}
                    className="w-full"
                    label="Type"
                    afterLabel={
                      <Tooltip>
                        <Tooltip.Trigger asChild>
                          <Button
                            variant="discreet"
                            iconLeft={faInfo}
                            size="square"
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Content>
                          <Text variant={TypographyStyles.BODY_SM}>
                            Labels this journal entry as one of these common
                            scenarios in the notes/details.
                          </Text>
                        </Tooltip.Content>
                      </Tooltip>
                    }
                  />
                </div>
                <div className="col-span-6">
                  <Form.Input
                    name="transactionId"
                    className="w-full"
                    label="Transaction ID"
                    afterLabel={
                      <Tooltip>
                        <Tooltip.Trigger asChild>
                          <Button
                            variant="discreet"
                            iconLeft={faInfo}
                            size="square"
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Content>
                          <Text variant={TypographyStyles.BODY_SM}>
                            Link this journal entry to a transaction by entering
                            its transaction ID.
                          </Text>
                        </Tooltip.Content>
                      </Tooltip>
                    }
                    iconRight={transactionObj.description ? faCheck : undefined}
                    iconRightClassName="bg-green-500"
                    schema={yup
                      .string()
                      .nullable()
                      .matches(
                        /^$|^[0-9]+$/,
                        'Please enter a valid number (no decimals)'
                      )}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <Form.Textarea
                  name={getFieldName<typeof formik.values>('details')}
                  label="Notes/Details"
                  schema={yup.string().nullable()}
                />
              </div>
              <div className="col-span-6">
                {transactionObj.description && (
                  <Text variant={TypographyStyles.BODY_SM}>
                    {transactionObj.description}
                  </Text>
                )}
              </div>
            </div>
          </div>

          <Dialog.Footer className="mt-8 flex justify-between">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => formik.handleSubmit()}
              loading={loading}
              disabled={totalError || Boolean(transactionObj.error)}
              type="submit"
            >
              {submitButtonText}
            </Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
